import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, HeartHandshake, Radio, Star, Loader2, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { countryList } from "@/lib/countries";
import { interestOptions } from "@/lib/interests";
import { holdNativePrompts, releaseNativePrompts } from "@/lib/nativePrompts";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { NearestCity } from "@/components/NearestCity";
import { ImageCropUpload } from "@/components/ImageCropUpload";

// Libellés des éléments du profil (carte + feuille) — mêmes clés que le serveur (profile_completion.py).
export const useCompletionLabels = () => {
  const { t } = useApp();
  return {
    identifier: t("profile_done.item_identifier", "E-mail ou WhatsApp vérifié"),
    name: t("profile_done.item_name", "Prénom et nom"),
    denomination: t("profile_done.item_denomination", "Obédience"),
    birth_date: t("profile_done.item_birth", "Date de naissance"),
    gender: t("profile_done.item_gender", "Genre"),
    location: t("profile_done.item_location", "Pays et ville"),
    role: t("profile_done.item_role", "Rôle dans l'église"),
    interests: t("profile_done.item_interests", "Centres d'intérêt (3 minimum)"),
    avatar: t("profile_done.item_avatar", "Photo de profil"),
    bio: t("profile_done.item_bio", "Quelques mots sur toi"),
  };
};

const ORDER = ["gender", "location", "role", "interests", "avatar", "bio"];
const ROLE_ICONS = { user: Users, preacher: HeartHandshake, artist: Radio, influencer: Star };

// Feuille « Compléter mon profil » (lot 580) : une étape à la fois, dans l'ordre des éléments manquants (obligatoires
// d'abord, puis photo / bio facultatives « Passer »). Chaque étape enregistre tout de suite (PATCH /users/me ou
// POST /users/me/role-intent) puis relit le profil → la carte et la garde se mettent à jour d'elles-mêmes.
export const ProfileCompletionSheet = ({ open, onOpenChange, reason = "card" }) => {
  const { user, t, lang, config, refreshMe } = useApp();
  const labels = useCompletionLabels();
  const pc = user?.profile_completion;
  // Lot 589 : feuille ouverte → aucun dialogue natif (ATT, consentement pubs, notifications) ne vient par-dessus.
  useEffect(() => {
    if (open) holdNativePrompts("profile_sheet"); else releaseNativePrompts("profile_sheet");
    return () => releaseNativePrompts("profile_sheet");
  }, [open]);
  const [skipped, setSkipped] = useState([]);
  const [busy, setBusy] = useState(false);
  const [gender, setGender] = useState(user?.gender || "");
  const [country, setCountry] = useState(user?.country || "");
  const [city, setCity] = useState(user?.city || "");
  const [role, setRole] = useState("user");
  const [interests, setInterests] = useState(user?.interests || []);
  const [bio, setBio] = useState(user?.bio || "");
  useEffect(() => { if (open) setSkipped([]); }, [open]);

  const missing = useMemo(() => {
    if (!pc) return [];
    const todo = pc.items.filter((i) => !i.done && !skipped.includes(i.key)).map((i) => i.key);
    return ORDER.filter((k) => todo.includes(k));
  }, [pc, skipped]);
  const stepKey = missing[0] || null;
  const requiredLeft = pc ? pc.missing_required.length : 0;

  const save = async (patch) => {
    setBusy(true);
    try {
      await api.patch("/users/me", patch);
      await refreshMe();
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };
  const chooseRole = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/users/me/role-intent", { role });
      if (data.kyc_status === "requested") toast.success(t("profile_done.role_requested", "Demande envoyée : un admin validera ton rôle."));
      await refreshMe();
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };
  const toggleInterest = (k) => setInterests((l) => (l.includes(k) ? l.filter((x) => x !== k) : [...l, k]));

  if (!pc) return null;
  const roles = [
    { key: "user", label: t("profile_done.role_user", "Membre") },
    { key: "preacher", label: t("role.preacher", "Prédicateur") },
    { key: "artist", label: t("role.artist", "Artiste") },
    { key: "influencer", label: t("role.influencer", "Influenceur(e)") },
  ];
  const Skip = ({ k }) => (
    <Button variant="outline" className="rounded-full" onClick={() => setSkipped((s) => [...s, k])} disabled={busy} data-testid="profile-done-skip">{t("onboarding.skip", "Passer")}</Button>
  );
  const Next = ({ onClick, disabled, label }) => (
    <Button className="pill-primary rounded-full flex-1 h-11" onClick={onClick} disabled={busy || disabled} data-testid="profile-done-next">
      {busy ? <Loader2 size={16} className="animate-spin" /> : (label || t("onboarding.next", "Suivant"))}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Lot 620 — au-dessus de TOUTES les feuilles (« Qui a réagi », partage, commentaires… z-[130]) : l'action refusée vient souvent de l'une d'elles. */}
      <DialogContent className="max-w-md z-[150]" overlayClassName="z-[150]" data-testid="profile-done-sheet" data-step={stepKey || "done"} data-reason={reason}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {reason === "gate" && <Lock size={18} style={{ color: "var(--yalola-primary)" }} />}
            {reason === "gate" ? t("profile_done.gate_title", "Complète ton profil pour interagir") : t("profile_done.title", "Complète ton profil")}
          </DialogTitle>
          <DialogDescription data-testid="profile-done-progress">
            {pc.percent} % · {requiredLeft > 0
              ? t("profile_done.required_left", "Encore {n} étape(s) obligatoire(s) pour publier, commenter, aimer et suivre").replace("{n}", String(requiredLeft))
              : t("profile_done.unlocked", "Tout est débloqué ! Il ne reste que des éléments facultatifs.")}
          </DialogDescription>
        </DialogHeader>

        {!stepKey && (
          <div className="text-center py-4 space-y-3" data-testid="profile-done-finished">
            <CheckCircle2 size={44} className="mx-auto" style={{ color: "var(--yalola-primary)" }} />
            <p className="font-semibold">{pc.percent >= 100 ? t("profile_done.complete", "Bravo, ton profil est complet !") : t("profile_done.unlocked_short", "Tu peux maintenant publier, commenter, aimer et suivre.")}</p>
            <Button className="pill-primary rounded-full h-11 px-6" onClick={() => onOpenChange(false)} data-testid="profile-done-close">{t("common.close", "Fermer")}</Button>
          </div>
        )}

        {stepKey === "gender" && (
          <div className="space-y-3">
            <Label>{labels.gender} *</Label>
            <div className="grid grid-cols-2 gap-2">
              {[["male", t("onboarding.male", "Homme")], ["female", t("onboarding.female", "Femme")]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setGender(k)} className="p-3 rounded-xl border text-sm font-medium" style={{ borderColor: gender === k ? "var(--yalola-primary)" : "var(--yalola-border)", background: gender === k ? "var(--yalola-surface-alt)" : "white", color: gender === k ? "var(--yalola-primary)" : "var(--yalola-text)" }} data-testid={`profile-done-gender-${k}`}>{l}</button>
              ))}
            </div>
            <div className="flex gap-2"><Next onClick={() => save({ gender })} disabled={!gender} /></div>
          </div>
        )}

        {stepKey === "location" && (
          <div className="space-y-3">
            <div>
              <Label>{t("onboarding.country", "Pays")} *</Label>
              <Select value={country || undefined} onValueChange={(v) => { setCountry(v); setCity(""); }}>
                <SelectTrigger data-testid="profile-done-country"><SelectValue placeholder={t("onboarding.choose", "Choisir…")} /></SelectTrigger>
                <SelectContent className="max-h-64 z-[160]">{countryList(lang).map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t("onboarding.city", "Ville")} *</Label><CityAutocomplete value={city} country={country} onChange={setCity} testId="profile-done-city" /></div>
            {/* Lot 659 : pays + ville en un tap depuis la position du téléphone */}
            <NearestCity onPick={(p) => { setCountry(p.country); setCity(p.city); }} testId="profile-done-nearest" />
            <div className="flex gap-2"><Next onClick={() => save({ country, city: city.trim() })} disabled={!country || !city.trim()} /></div>
          </div>
        )}

        {stepKey === "role" && (
          <div className="space-y-3">
            <Label>{labels.role} *</Label>
            <div className="grid grid-cols-2 gap-2">
              {roles.map(({ key, label }) => { const Icon = ROLE_ICONS[key]; return (
                <button key={key} type="button" onClick={() => setRole(key)} className="p-3 rounded-xl border text-xs font-medium flex flex-col items-center gap-1" style={{ borderColor: role === key ? "var(--yalola-primary)" : "var(--yalola-border)", background: role === key ? "var(--yalola-surface-alt)" : "white", color: role === key ? "var(--yalola-primary)" : "var(--yalola-text)" }} data-testid={`profile-done-role-${key}`}>
                  <Icon size={16} /><span>{label}</span>
                </button>
              ); })}
            </div>
            {role !== "user" && <p className="text-[11px] rounded-lg px-3 py-2" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-text-muted)" }} data-testid="profile-done-role-hint">{t("profile_done.role_hint", "Les rôles Prédicateur, Artiste et Influenceur sont validés par un admin (directs, offrandes). Tu restes membre en attendant.")}</p>}
            <div className="flex gap-2"><Next onClick={chooseRole} label={t("common.save", "Enregistrer")} /></div>
          </div>
        )}

        {stepKey === "interests" && (
          <div className="space-y-3">
            <Label>{labels.interests} — {interests.length}/3</Label>
            <div className="flex flex-wrap gap-1.5 max-h-56 overflow-y-auto" data-testid="profile-done-interests">
              {interestOptions(config, lang).map(({ key, label }) => (
                <button key={key} type="button" onClick={() => toggleInterest(key)} className={`chip ${interests.includes(key) ? "!bg-[color:var(--yalola-primary)] !text-white !border-transparent" : ""}`} data-testid={`profile-done-interest-${key}`}>{label}</button>
              ))}
            </div>
            <div className="flex gap-2"><Next onClick={() => save({ interests })} disabled={interests.length < 3} /></div>
          </div>
        )}

        {stepKey === "avatar" && (
          <div className="space-y-3 text-center">
            <Label>{labels.avatar} <span style={{ color: "var(--yalola-text-muted)" }}>({t("onboarding.optional", "facultatif")})</span></Label>
            <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>{t("profile_done.avatar_hint", "Une vraie photo : tes frères et sœurs te reconnaissent et te suivent 3× plus.")}</p>
            <div className="flex justify-center"><ImageCropUpload aspect={1} cropShape="round" primary buttonLabel={t("profile_done.avatar_pick", "Choisir une photo")} onUploaded={(url) => save({ avatar_url: url })} testId="profile-done-avatar" title={t("profile.adjust_avatar", "Ajuster la photo de profil")} /></div>
            <div className="flex justify-center"><Skip k="avatar" /></div>
          </div>
        )}

        {stepKey === "bio" && (
          <div className="space-y-3">
            <Label>{labels.bio} <span style={{ color: "var(--yalola-text-muted)" }}>({t("onboarding.optional", "facultatif")})</span></Label>
            <Textarea value={bio} onChange={(e) => setBio(e.target.value.slice(0, 200))} rows={3} placeholder={t("profile_done.bio_ph", "Ex. : Servante de Dieu, chorale de l'église de Limete…")} data-testid="profile-done-bio" />
            <div className="flex gap-2"><Skip k="bio" /><Next onClick={() => save({ bio: bio.trim() })} disabled={!bio.trim()} label={t("common.save", "Enregistrer")} /></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
